import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWithAuth } from "../utils/api";
import { getHistory, recordVisit, recordSearch, clearHistory, subscribeHistory } from "../utils/searchHistory";

/**
 * useGlobalSearch — state and behaviour behind the sidebar ⌘K box.
 *
 *   - debounced (250 ms), min 2 chars, each keystroke cancels the previous
 *     request and stale responses are dropped by sequence number
 *   - results arrive grouped (customers / orders / vessels / appointments /
 *     team) and are flattened in that order for ↑ ↓ ↵ navigation
 *   - "Go to" page matches come from PAGE_CATALOG below, filtered by the
 *     user's permissions, so typing "sett" jumps to Settings
 *   - while the box is empty it shows this browser's history (utils/
 *     searchHistory.js): recent visits, recent searches and most-visited
 *
 * Pure state — the dropdown (components/SearchResults.js) renders it, the
 * layout wires the input.
 */

export const PAGE_CATALOG = [
  { key: "dashboard", label: "Dashboard", perms: ["dashboard.access"] },
  { key: "customers", label: "Customers", perms: ["customers.access"] },
  { key: "vessels", label: "Vessels", perms: ["vessels.access"] },
  { key: "order-workflow", label: "Orders & Workflow", perms: ["workflow.access"] },
  { key: "payment-terminal", label: "Payment Terminal", perms: ["terminal.access"] },
  { key: "appointments", label: "Appointments", perms: ["appointments.access"] },
  { key: "timeclocks", label: "Time Clocks", perms: ["timeclocks.access"] },
  { key: "poseidon-plus", label: "Poseidon+", perms: ["plus.access"] },
  { key: "shopmonkey", label: "Poseidon Live", perms: ["shopmonkey.access"] },
  { key: "sales-intake", label: "Sales Intake", perms: ["calls.access", "osr.access", "sales_estimates.access"] },
  { key: "sales-submissions", label: "Sales Sheets", perms: ["sales_submissions.access"] },
  { key: "customer-service", label: "Customer Service", perms: ["customer_service.access"] },
  { key: "dig", label: "Dig", perms: ["dig.access"] },
  { key: "reports", label: "Reports", perms: ["reports.access"] },
  { key: "marketing", label: "Marketing Hub", perms: ["marketing.hub.access"] },
  { key: "parts-inventory", label: "Parts & Inventory", perms: ["parts_inventory.access", "parts_orders.access"] },
  { key: "settings", label: "Settings", perms: ["settings.access"] },
];

export const GROUP_ORDER = ["pages", "customers", "orders", "vehicles", "appointments", "users"];
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const PER_GROUP = 6;

const emptyResults = () => Object.fromEntries(GROUP_ORDER.map((g) => [g, []]));

export default function useGlobalSearch({ can } = {}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [counts, setCounts] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [history, setHistory] = useState(getHistory);
  useEffect(() => subscribeHistory(() => setHistory(getHistory())), []);
  useEffect(() => { if (open) setHistory(getHistory()); }, [open]); // fresh on every open
  const seqRef = useRef(0);
  const abortRef = useRef(null);

  const trimmed = query.trim();

  // Pages the person can open whose name contains the query.
  const pages = useMemo(() => {
    if (trimmed.length < MIN_CHARS || typeof can !== "function") return [];
    const needle = trimmed.toLowerCase();
    return PAGE_CATALOG.filter(
      (p) => p.label.toLowerCase().includes(needle) && p.perms.some((perm) => can(perm)),
    ).map((p) => ({ type: "page", id: p.key, title: p.label, url: `/app/${p.key}` }));
  }, [trimmed, can]);

  useEffect(() => {
    if (trimmed.length < MIN_CHARS) {
      abortRef.current?.abort();
      setResults(null);
      setCounts(null);
      setLoading(false);
      return undefined;
    }
    const seq = ++seqRef.current;
    // Busy from the keystroke, not from the end of the debounce: otherwise the
    // previous query's results sit there for 250 ms looking current.
    setLoading(true);
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetchWithAuth(`/admin/search?q=${encodeURIComponent(trimmed)}&limit=${PER_GROUP}`, {
          signal: controller.signal,
        });
        if (seq !== seqRef.current) return; // a newer keystroke owns the box now
        if (!res.ok) throw new Error(`search ${res.status}`);
        const data = await res.json();
        if (seq !== seqRef.current) return;
        setResults({ ...emptyResults(), ...(data.results || {}) });
        setCounts(data.counts || {});
      } catch (err) {
        if (err?.name === "AbortError" || seq !== seqRef.current) return;
        setResults(emptyResults());
        setCounts({});
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  // One flat, ordered list for the keyboard: pages first, then each group.
  const groups = useMemo(() => {
    const out = [];
    if (pages.length) out.push({ key: "pages", items: pages, count: pages.length });
    if (results) {
      for (const key of GROUP_ORDER) {
        if (key === "pages") continue;
        const items = results[key] || [];
        if (items.length) out.push({ key, items, count: counts?.[key] ?? items.length });
      }
    }
    return out;
  }, [pages, results, counts]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  // Empty-state rows, in keyboard order: recent visits, recent searches, most visited.
  const historyItems = useMemo(
    () => [
      ...history.recent,
      ...history.searches.map((q) => ({ type: "search", id: q, title: q })),
      ...history.frequent,
    ],
    [history],
  );
  const showingHistory = trimmed.length < MIN_CHARS && historyItems.length > 0;
  const navItems = showingHistory ? historyItems : flat;

  // Reset the active row only when the LIST changes (not on every render —
  // a parent re-render would otherwise snap ↑↓ back to the top).
  const navKey = navItems.map((i) => `${i.type}:${i.id}`).join("|");
  useEffect(() => { setActiveIndex(navKey ? 0 : -1); }, [navKey]);

  // Opening a result remembers both the thing and the query that found it.
  const remember = useCallback((item) => {
    if (!item || item.type === "search") return;
    recordVisit(item);
    if (trimmed.length >= MIN_CHARS) recordSearch(trimmed);
  }, [trimmed]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setCounts(null);
    abortRef.current?.abort();
  }, []);

  /** Keyboard handler for the input: ↑ ↓ move, ↵ opens, Esc closes. */
  const onKeyDown = useCallback(
    (e, onSelect) => {
      if (!open) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!navItems.length) return;
        e.preventDefault();
        setActiveIndex((i) => {
          const n = navItems.length;
          return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
        });
      } else if (e.key === "Enter") {
        const item = navItems[activeIndex];
        if (item) { e.preventDefault(); onSelect(item); }
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
        e.currentTarget?.blur?.();
      }
    },
    [open, navItems, activeIndex, close],
  );

  return {
    query, setQuery, open, setOpen, close,
    loading, groups, flat, counts,
    history, showingHistory, clearHistory, remember,
    activeIndex, setActiveIndex, navItems, onKeyDown,
    minChars: MIN_CHARS,
  };
}
